import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronDown, ChevronRight, Settings } from 'lucide-react'
import { useBot, useUpdateBot } from '@/shared/hooks/useBots'
import { useTelegram } from '@/shared/hooks/useTelegram'
import { useBackButton } from '@/shared/hooks/useBackButton'
import { PricingConfig, PriceRange, GamblingPricing } from '@/shared/types'

export default function BotAutoResources() {
  const { botId } = useParams<{ botId: string }>()
  const navigate = useNavigate()
  const { isReady, hapticFeedback, hapticNotification, showMainButton, hideMainButton, setMainButtonLoading } = useTelegram()
  const { data: bot, isLoading } = useBot(botId)
  const updateBot = useUpdateBot()

  const [autoResource, setAutoResource] = useState(bot?.auto_resource ?? true)
  const [contentType, setContentType] = useState<'channels_only' | 'bots_only' | 'everything'>(
    bot?.content_type || 'everything'
  )
  const [boostsEnabled, setBoostsEnabled] = useState(bot?.boosts_enabled || false)
  const [privateBoosts, setPrivateBoosts] = useState(bot?.private_boosts || false)
  const [showsEnabled, setShowsEnabled] = useState(bot?.shows_enabled || false)
  const [gamblingEnabled, setGamblingEnabled] = useState(false)
  const [ukrainianEnabled, setUkrainianEnabled] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [gamblingExpanded, setGamblingExpanded] = useState(false)

  // Language pricing state - start with Russian language
  const [selectedLanguage, setSelectedLanguage] = useState('ru')
  const [pricingConfig, setPricingConfig] = useState<PricingConfig>(
    bot?.pricing_config || {
      ru: {
        bot_price: { min: 0, max: 100, prem_min: 0, prem_max: 100 },
        channel_price: { min: 0, max: 100, prem_min: 0, prem_max: 100 },
        show_price: { min: 0, max: 100, prem_min: 0, prem_max: 100 },
        boost_price: { min: 0, max: 100, prem_min: 0, prem_max: 100 },
        gambling_price: {
          bot_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
          show_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
          channel_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
          boost_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
        },
      },
    }
  )

  // Initialize state from bot data
  useEffect(() => {
    if (bot) {
      setAutoResource(bot.auto_resource ?? true)
      setContentType(bot.content_type || 'everything')
      setBoostsEnabled(bot.boosts_enabled || false)
      setPrivateBoosts(bot.private_boosts || false)
      setShowsEnabled(bot.shows_enabled || false)
      setGamblingEnabled(bot.gambling_enabled || false)
      setUkrainianEnabled(bot.ukrainian_enabled || false)
      setPricingConfig(bot.pricing_config || {
        ru: {
          bot_price: { min: 0, max: 100, prem_min: 0, prem_max: 100 },
          channel_price: { min: 0, max: 100, prem_min: 0, prem_max: 100 },
          show_price: { min: 0, max: 100, prem_min: 0, prem_max: 100 },
          boost_price: { min: 0, max: 100, prem_min: 0, prem_max: 100 },
          gambling_price: {
            bot_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
            show_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
            channel_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
            boost_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
          },
        },
      })
    }
  }, [bot])

  // Reset success state after 2 seconds
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [showSuccess])

  // Store handleSave ref to avoid recreating MainButton
  const handleSaveRef = useRef<() => Promise<void>>()

  const handleSave = async () => {
    if (!bot) return

    hapticFeedback('medium')
    setMainButtonLoading(true)
    try {
      // Only update editable price fields (bot_price, channel_price, show_price)
      // Keep boost_price and gambling_price from original bot config
      const cleanedPricingConfig: PricingConfig = {}

      // Helper to validate and fix price range
      const validatePriceRange = (priceRange: PriceRange | undefined): PriceRange => {
        if (!priceRange) {
          return { min: 0, max: 0, prem_min: 0, prem_max: 0 }
        }

        // Ensure min is not greater than max
        const min = priceRange.min || 0
        const max = priceRange.max || 0

        // If min > max, set both to the same value (use max as it's likely correct)
        if (min > max) {
          return {
            min: max,
            max: max,
            prem_min: priceRange.prem_min || 0,
            prem_max: priceRange.prem_max || 0,
          }
        }

        return {
          min: min,
          max: max,
          prem_min: priceRange.prem_min || 0,
          prem_max: priceRange.prem_max || 0,
        }
      }

      for (const lang of ['ru', 'en']) {
        cleanedPricingConfig[lang] = {
          bot_price: pricingConfig[lang]?.bot_price || bot.pricing_config?.[lang]?.bot_price || { min: 0, max: 0, prem_min: 0, prem_max: 0 },
          channel_price: pricingConfig[lang]?.channel_price || bot.pricing_config?.[lang]?.channel_price || { min: 0, max: 0, prem_min: 0, prem_max: 0 },
          show_price: pricingConfig[lang]?.show_price || bot.pricing_config?.[lang]?.show_price || { min: 0, max: 0, prem_min: 0, prem_max: 0 },
          // Validate and fix boost price from original config
          boost_price: validatePriceRange(bot.pricing_config?.[lang]?.boost_price),
          // Preserve nested gambling_price structure from original config (read-only, system-managed)
          gambling_price: bot.pricing_config?.[lang]?.gambling_price || {
            bot_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
            show_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
            channel_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
            boost_price: { min: 0, max: 0, prem_min: 0, prem_max: 0 },
          },
        }
      }

      await updateBot.mutateAsync({
        botId: bot.id,
        data: {
          auto_resource: autoResource,
          content_type: contentType,
          boosts_enabled: boostsEnabled,
          private_boosts: privateBoosts,
          shows_enabled: showsEnabled,
          gambling_enabled: gamblingEnabled,
          ukrainian_enabled: ukrainianEnabled,
          pricing_config: cleanedPricingConfig,
        },
      })
      hapticNotification('success')
      setShowSuccess(true)

      // Reset loading state and hide main button before navigation
      setMainButtonLoading(false)
      hideMainButton()
      document.body.style.backgroundColor = ''

      // Scroll to top before navigation
      window.scrollTo(0, 0)

      // Navigate back to bot details page with success message
      navigate(`/bots/${botId}`, {
        replace: true,
        state: { successMessage: 'Бот успешно обновлен' }
      })
    } catch (error) {
      console.error('Failed to update bot:', error)
      hapticNotification('error')
      setMainButtonLoading(false)
    }
  }

  // Update ref with latest handleSave
  handleSaveRef.current = handleSave

  // Auto-manage back button
  useBackButton()

  // Setup MainButton and page background
  useEffect(() => {
    if (!isReady) return

    // Scroll to top on mount
    window.scrollTo(0, 0)

    const onSave = () => {
      handleSaveRef.current?.()
    }

    // Reset loading state and show button
    setMainButtonLoading(false)
    showMainButton('Сохранить настройки', onSave, '#5288c1', '#ffffff')

    // Set page background color like bottom nav
    document.body.style.backgroundColor = '#1e2832'

    return () => {
      hideMainButton()
      document.body.style.backgroundColor = ''
    }
  }, [isReady, showMainButton, hideMainButton, setMainButtonLoading])

  const updatePriceRange = (
    resourceType: 'bot_price' | 'channel_price' | 'show_price' | 'boost_price' | 'gambling_price',
    field: keyof PriceRange,
    value: number
  ) => {
    setPricingConfig((prev) => {
      const currentRange = prev[selectedLanguage]?.[resourceType] || {}

      // When updating min or max, update both to the same value to keep them in sync
      if (field === 'min' || field === 'max') {
        return {
          ...prev,
          [selectedLanguage]: {
            ...prev[selectedLanguage],
            [resourceType]: {
              ...currentRange,
              min: value,
              max: value,
            },
          },
        }
      }

      // For prem_min/prem_max, update individually
      return {
        ...prev,
        [selectedLanguage]: {
          ...prev[selectedLanguage],
          [resourceType]: {
            ...currentRange,
            [field]: value,
          },
        },
      }
    })
  }

  if (isLoading || !bot) {
    return <div className="min-h-screen bg-tg-bg flex items-center justify-center">Loading...</div>
  }

  const currentLanguageConfig = pricingConfig[selectedLanguage] || {}

  return (
    <div className="min-h-screen bg-tg-bg pb-24 pt-[96px]">
      {/* Header */}
      <div className="bg-tg-secondary-bg border-b border-tg-secondary-bg">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-tg-text">Авто-ресурсы</h1>
              <p className="text-sm text-tg-hint mt-0.5">Фильтры и ценообразование</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {/* Auto-Resource Toggle */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm border border-tg-secondary-bg overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Режим работы</h2>
            <p className="text-xs text-tg-hint mt-1">Как выбирать ресурсы для пользователей</p>
          </div>

          <div className="flex items-center justify-between p-4">
            <div>
              <div className="font-medium text-tg-text">Авто-ресурс</div>
              <div className="text-xs text-tg-hint mt-0.5">
                {autoResource ? 'Автоматический подбор всех ресурсов' : 'Только выбранные вами ресурсы'}
              </div>
            </div>
            <button
              onClick={() => {
                hapticFeedback('light')
                setAutoResource(!autoResource)
              }}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                autoResource ? 'bg-[#059669]' : 'bg-tg-bg'
              }`}
            >
              <div
                className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  autoResource ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Content Type Filter */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm border border-tg-secondary-bg overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Тип контента</h2>
            <p className="text-xs text-tg-hint mt-1">Какие ресурсы показывать пользователям</p>
          </div>

          <div className="p-4 space-y-2">
            {[
              { value: 'everything', label: 'Всё', desc: 'Боты и каналы' },
              { value: 'channels_only', label: 'Только каналы', desc: 'Показывать только каналы' },
              { value: 'bots_only', label: 'Только боты', desc: 'Показывать только ботов' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => {
                  hapticFeedback('light')
                  setContentType(option.value as any)
                }}
                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                  contentType === option.value
                    ? 'border-tg-button bg-tg-button/10'
                    : 'border-[#2a3441] hover:border-[#3a4551]'
                }`}
              >
                <div className="font-medium text-tg-text">{option.label}</div>
                <div className="text-xs text-tg-hint mt-0.5">{option.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Toggles */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm border border-tg-secondary-bg overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Дополнительные опции</h2>
          </div>

          <div className="divide-y divide-tg-secondary-bg">
            <div className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium text-tg-text">Буст-задачи</div>
                <div className="text-xs text-tg-hint">Показывать задачи на буст каналов</div>
              </div>
              <button
                onClick={() => {
                  hapticFeedback('light')
                  setBoostsEnabled(!boostsEnabled)
                }}
                className={`relative w-12 h-7 rounded-full transition-colors ${
                  boostsEnabled ? 'bg-[#059669]' : 'bg-tg-bg'
                }`}
              >
                <div
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    boostsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {boostsEnabled && (
              <div className="flex items-center justify-between p-4 pl-8">
                <div>
                  <div className="font-medium text-tg-text">Приватные бусты</div>
                  <div className="text-xs text-tg-hint">Включая приватные каналы</div>
                </div>
                <button
                  onClick={() => {
                    hapticFeedback('light')
                    setPrivateBoosts(!privateBoosts)
                  }}
                  className={`relative w-12 h-7 rounded-full transition-colors ${
                    privateBoosts ? 'bg-[#059669]' : 'bg-tg-bg'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      privateBoosts ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            )}

            <div className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium text-tg-text">Показы</div>
                <div className="text-xs text-tg-hint">Задачи на просмотр контента</div>
              </div>
              <button
                onClick={() => {
                  hapticFeedback('light')
                  setShowsEnabled(!showsEnabled)
                }}
                className={`relative w-12 h-7 rounded-full transition-colors ${
                  showsEnabled ? 'bg-[#059669]' : 'bg-tg-bg'
                }`}
              >
                <div
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    showsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium text-tg-text">Гемблинг</div>
                <div className="text-xs text-tg-hint">Задачи с гемблинг категорией</div>
              </div>
              <button
                onClick={() => {
                  hapticFeedback('light')
                  setGamblingEnabled(!gamblingEnabled)
                }}
                className={`relative w-12 h-7 rounded-full transition-colors ${
                  gamblingEnabled ? 'bg-[#059669]' : 'bg-tg-bg'
                }`}
              >
                <div
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    gamblingEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center justify-between p-4">
              <div>
                <div className="font-medium text-tg-text">УК ресурсы</div>
                <div className="text-xs text-tg-hint">Задачи от украинских ресурсов</div>
              </div>
              <button
                onClick={() => {
                  hapticFeedback('light')
                  setUkrainianEnabled(!ukrainianEnabled)
                }}
                className={`relative w-12 h-7 rounded-full transition-colors ${
                  ukrainianEnabled ? 'bg-[#059669]' : 'bg-tg-bg'
                }`}
              >
                <div
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${
                    ukrainianEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Pricing Configuration */}
        <div className="bg-tg-secondary-bg rounded-2xl shadow-sm border border-tg-secondary-bg overflow-hidden">
          <div className="p-4 border-b border-tg-secondary-bg">
            <h2 className="font-semibold text-tg-text">Ценообразование</h2>
            <p className="text-xs text-tg-hint mt-1">Минимальная цена для разных типов задач</p>
          </div>

          <div className="p-4 space-y-3">
            {/* Language Tabs */}
            <div className="flex gap-2 p-1 bg-tg-bg rounded-xl">
              {[
                { value: 'ru', label: 'RU' },
                { value: 'en', label: 'EN' },
              ].map((lang) => (
                <button
                  key={lang.value}
                  onClick={() => {
                    hapticFeedback('light')
                    setSelectedLanguage(lang.value)
                  }}
                  className={`flex-1 py-2 px-4 rounded-lg font-medium transition-all ${
                    selectedLanguage === lang.value
                      ? 'bg-tg-secondary-bg text-tg-text shadow-sm'
                      : 'text-tg-hint hover:text-tg-text'
                  }`}
                >
                  {lang.label}
                </button>
              ))}
            </div>

            {/* Price Ranges with Sliders */}
            {[
              { key: 'channel_price', label: 'Каналы', icon: '📢', editable: true, color: '#059669' },
              { key: 'bot_price', label: 'Боты', icon: '🤖', editable: true, color: '#3b82f6' },
              { key: 'show_price', label: 'Показы', icon: '👁️', editable: true, color: '#8b5cf6' },
              { key: 'boost_price', label: 'Бусты', icon: '🚀', editable: false, color: '#f59e0b' },
            ].map((type) => {
              const priceRange = (currentLanguageConfig[type.key as keyof typeof currentLanguageConfig] || {
                min: 0,
                max: 0,
                prem_min: 0,
                prem_max: 0
              }) as PriceRange
              const currentValue = priceRange.min || 0
              const premMin = priceRange.prem_min || 0
              const premMax = priceRange.prem_max || 0

              // Define price ranges based on language and type
              const getPriceButtons = () => {
                if (!type.editable) return []

                if (selectedLanguage === 'ru') {
                  if (type.key === 'channel_price') return [1.5, 1.6, 1.7, 1.8, 1.9]
                  if (type.key === 'bot_price') return [1.0, 1.1, 1.2, 1.25]
                  if (type.key === 'show_price') return [1.0, 1.1, 1.2, 1.25]
                } else if (selectedLanguage === 'en') {
                  if (type.key === 'channel_price') return [1.1, 1.3, 1.5, 1.7, 1.9]
                  if (type.key === 'bot_price') return [0.5, 0.65, 0.8, 1.0, 1.25]
                  if (type.key === 'show_price') return [0.5, 0.6, 0.8, 1.0, 1.25]
                }
                return []
              }

              const priceButtons = getPriceButtons()

              // Slider min/max match the preset button values
              const sliderMin = priceButtons.length > 0 ? Math.min(...priceButtons) : 0
              const sliderMax = priceButtons.length > 0 ? Math.max(...priceButtons) : 5
              const sliderStep = 0.01

              return (
                <div
                  key={type.key}
                  className="rounded-xl p-3 space-y-2 border-2 transition-all"
                  style={{
                    borderColor: `${type.color}33`,
                    backgroundColor: `${type.color}0d`
                  }}
                >
                  {/* Header with Icon, Label and Price */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-lg"
                        style={{ backgroundColor: `${type.color}1a` }}
                      >
                        {type.icon}
                      </div>
                      <span className="font-semibold text-tg-text text-sm">{type.label}</span>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div className="text-base font-bold" style={{ color: type.color }}>
                        {currentValue > 0 ? `${currentValue.toFixed(2)} ₽` : '—'}
                      </div>
                      {(premMin > 0 || premMax > 0) && (
                        <div className="text-xs text-amber-500">
                          ★{premMin.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>

                  {type.editable ? (
                    <>
                      {/* Slider */}
                      <div className="relative">
                        <input
                          type="range"
                          min={sliderMin}
                          max={sliderMax}
                          step={sliderStep}
                          value={currentValue}
                          onChange={(e) => {
                            const newValue = parseFloat(e.target.value)
                            updatePriceRange(type.key as any, 'min', newValue)
                            updatePriceRange(type.key as any, 'max', newValue)
                          }}
                          onTouchStart={() => hapticFeedback('light')}
                          className="w-full h-2 bg-tg-hint/20 rounded-lg appearance-none cursor-pointer slider-thumb"
                          style={{
                            background: `linear-gradient(to right, ${type.color} 0%, ${type.color} ${((currentValue - sliderMin) / (sliderMax - sliderMin)) * 100}%, rgba(168, 179, 207, 0.2) ${((currentValue - sliderMin) / (sliderMax - sliderMin)) * 100}%, rgba(168, 179, 207, 0.2) 100%)`,
                            ['--slider-color' as any]: type.color
                          }}
                        />
                      </div>

                      {/* Quick select buttons */}
                      {priceButtons.length > 0 && (
                        <div className="flex gap-1.5">
                          {priceButtons.map((val) => (
                            <button
                              key={val}
                              onClick={() => {
                                hapticFeedback('light')
                                updatePriceRange(type.key as any, 'min', val)
                                updatePriceRange(type.key as any, 'max', val)
                              }}
                              className="flex-1 py-1.5 text-xs font-semibold rounded-lg transition-all"
                              style={
                                Math.abs(currentValue - val) < 0.01
                                  ? { backgroundColor: type.color, color: '#ffffff' }
                                  : { backgroundColor: `${type.color}1a`, color: type.color }
                              }
                            >
                              {val}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-1">
                      <p className="text-xs text-tg-hint">Системная цена</p>
                    </div>
                  )}
                </div>
              )
            })}

            {/* Gambling Pricing Section - Nested Structure (Read-Only) */}
            <div className="space-y-3 pt-3 border-t border-tg-bg">
              <button
                onClick={() => {
                  hapticFeedback('light')
                  setGamblingExpanded(!gamblingExpanded)
                }}
                className="w-full flex items-center justify-between p-3 bg-tg-bg rounded-lg hover:bg-tg-bg/70 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xl">🎰</span>
                  <h3 className="font-medium text-tg-text">Гэмблинг</h3>
                  <span className="text-xs text-tg-hint">(системные цены)</span>
                </div>
                {gamblingExpanded ? (
                  <ChevronDown className="w-5 h-5 text-tg-hint" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-tg-hint" />
                )}
              </button>

              {gamblingExpanded && (
                <div className="space-y-3 pl-4">
                  {[
                    { key: 'bot_price', label: 'Боты', icon: '🤖' },
                    { key: 'show_price', label: 'Показы', icon: '👁️' },
                    { key: 'channel_price', label: 'Каналы', icon: '📢' },
                    { key: 'boost_price', label: 'Бусты', icon: '🚀' },
                  ].map((subType) => {
                    const gamblingPricing = currentLanguageConfig.gambling_price as GamblingPricing | undefined
                    const priceRange = gamblingPricing?.[subType.key as keyof GamblingPricing] || { min: 0, max: 0, prem_min: 0, prem_max: 0 }
                    const min = priceRange.min || 0
                    const max = priceRange.max || 0
                    const premMin = priceRange.prem_min || 0
                    const premMax = priceRange.prem_max || 0

                    return (
                      <div key={subType.key} className="flex items-center justify-between p-3 bg-tg-secondary-bg/50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-base">{subType.icon}</span>
                          <span className="text-sm font-medium text-tg-text">{subType.label}</span>
                        </div>
                        <div className="flex items-center gap-2 text-right">
                          {(min > 0 || max > 0) ? (
                            <>
                              <div className="text-sm font-semibold text-tg-text">
                                {Math.abs(min - max) < 0.01 ? `${min.toFixed(2)} ₽` : `${min.toFixed(2)}-${max.toFixed(2)} ₽`}
                              </div>
                              {(premMin > 0 || premMax > 0) && (
                                <div className="text-xs text-amber-500">
                                  ★ {Math.abs(premMin - premMax) < 0.01 ? premMin.toFixed(2) : `${premMin.toFixed(2)}-${premMax.toFixed(2)}`}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-sm text-tg-hint">—</div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
